
import { useState } from "react";
import { Header } from "@/components/Header";
import { useNavigate } from "react-router-dom";

// Custom hooks
import { useUserProfile } from "@/hooks/useUserProfile";
import { useServiceSuggestions } from "@/hooks/useServiceSuggestions";
import { useProductsState } from "@/hooks/useProductsState";
import { useServiceSaving } from "@/hooks/useServiceSaving";
import { useDescriptionGeneration } from "@/hooks/useDescriptionGeneration";
import { handleImageUpload, removeImage } from "@/utils/imageUtils";

// Components
import { ProgressIndicator } from "@/components/add-services/ProgressIndicator";
import { PageHeader } from "@/components/add-services/PageHeader";
import { ServiceSuggestions } from "@/components/add-services/ServiceSuggestions";
import { ProductForm } from "@/components/add-services/ProductForm";
import { AddProductButton } from "@/components/add-services/AddProductButton";
import { NavigationButtons } from "@/components/add-services/NavigationButtons";
import { BackButton } from "@/components/onboarding/BackButton";

// Types
import { ServiceSuggestion } from "@/types/services";

const AddServices = () => {
  const navigate = useNavigate();
  
  // Get user profile data
  const [profileData, isProfileLoading] = useUserProfile();
  const { userProfession, rawProfession, displayProfession, userCurrency, userCountry, preferredMessaging } = profileData;
  
  // Get service suggestions
  const { suggestions, loadingSuggestions } = useServiceSuggestions(userProfession);
  
  // Product state management
  const { 
    products, 
    setProducts, 
    isFormValid,
    handlePriceChange,
    addProduct,
    removeProduct,
    useSuggestion
  } = useProductsState();
  
  // Service saving logic
  const { isSubmitting, saveServices } = useServiceSaving();
  
  // Description generation
  const { 
    isGeneratingDescription, 
    descriptionProductIndex, 
    generateDescription 
  } = useDescriptionGeneration(products, setProducts, userProfession);
  
  // Handle image upload and removal
  const handleProductImageUpload = (productIndex: number, files: FileList) => {
    handleImageUpload(productIndex, files, products, setProducts);
  };
  
  const handleRemoveImage = (productIndex: number, imageIndex: number) => {
    removeImage(productIndex, imageIndex, products, setProducts);
  };
  
  // Save services wrapper
  const handleSaveServices = async () => {
    await saveServices(products);
  };

  // Determine previous route based on preferred messaging
  const previousRoute = preferredMessaging === 'telegram' ? '/telegram-connect' : '/onboarding';

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white relative">
      <Header />
      <BackButton previousRoute={previousRoute} className="md:fixed" />
      <div className="container mx-auto px-4 py-24 max-w-3xl">
        <ProgressIndicator currentStep={1} totalSteps={5} />

        <PageHeader 
          title="Add Your Services" 
          description="List your services, set pricing, and upload images to showcase your work" 
        />

        <ServiceSuggestions 
          suggestions={suggestions}
          loadingSuggestions={loadingSuggestions}
          userProfession={userProfession}
          rawProfession={rawProfession}
          userCurrency={userCurrency}
          useSuggestion={useSuggestion}
        />

        <div className="space-y-8">
          {products.map((product, index) => (
            <ProductForm
              key={index}
              product={product}
              index={index}
              userCurrency={userCurrency}
              handlePriceChange={handlePriceChange}
              handleImageUpload={handleProductImageUpload}
              removeImage={handleRemoveImage}
              removeProduct={removeProduct}
              generateDescription={generateDescription}
              isGeneratingDescription={isGeneratingDescription}
              descriptionProductIndex={descriptionProductIndex}
              setProducts={setProducts}
            />
          ))}
        </div>

        <AddProductButton onClick={addProduct} />

        <NavigationButtons 
          isSubmitting={isSubmitting}
          onSave={handleSaveServices}
          previousRoute={previousRoute}
          nextRoute="/payment-methods"
          isValid={isFormValid}
        />
      </div>
    </div>
  );
};

export default AddServices;
